import { normalizeToCamelCase } from './normalize';
import config from 'config/shared';
import { logErrorTimes, logTimes } from './logger';
import { getCache, setCache } from './cache';
import { encodeHeaders } from 'utils/encoding';
import { abortControllerPool } from './abortControllerPool';
import { ApiError } from 'utils/apiError';
import { enforceHttpsProtocol } from 'utils/enforceHttpsProtocol';
import { outgoingMetrics } from 'middleware/metrics';
import { isSSR } from 'utils/isSSR';

const CACHE_TTL = 1000 * 60 * 15;

const methods = {
	DELETE: async (url: string, input?: Record<string, string>, requestInit: RequestInit = {}) =>
		fetch(url, {
			method: 'DELETE',
			body: input instanceof FormData ? input : JSON.stringify(input),
			...requestInit,
		}),
	GET: async (url: string, input?: Record<string, string>, requestInit: RequestInit = {}) =>
		fetch(input && Object.keys(input).length ? `${url}?${new URLSearchParams(input).toString()}` : url, {
			...requestInit,
		}),
	PATCH: async (url: string, input?: Record<string, any>, requestInit: RequestInit = {}) =>
		fetch(url, {
			method: 'PATCH',
			body: input instanceof FormData ? input : JSON.stringify(input),
			...requestInit,
		}),
	POST: async (url: string, input?: Record<string, any>, requestInit: RequestInit = {}) =>
		fetch(url, {
			method: 'POST',
			body: input instanceof FormData ? input : JSON.stringify(input),
			...requestInit,
		}),
	PUT: async (url: string, input?: Record<string, any>, requestInit: RequestInit = {}) =>
		fetch(url, {
			method: 'PUT',
			body: input instanceof FormData ? input : JSON.stringify(input),
			...requestInit,
		}),
} as const;

const REQUEST_TIMEOUT = 7000;

export type Pagination = {
	total: number;
	totalWithUnpacked?: number;
	limit: number;
	offset: number;
};

type CreateAPIMethod = <TInput extends Record<string, any> | undefined = object, TOutput = Response>(
	opts: {
		apiPath?: '/api/v1' | '/api/v2';
		method: keyof typeof methods;
		headers?: HeadersInit | undefined;
		normalize?: (response: {
			categories?: any;
			results?: any;
			result?: any;
			pagination?: any;
			leaf_categories?: any;
		}) => TOutput;
		skipResponseParsing?: boolean;
		suppressErrorThrowing?: boolean;
		requestTimeout?: number;
		requestId?: ReturnType<typeof crypto.randomUUID>;
		abortController?: AbortController;
		useCache?: boolean;
		cacheKeyPostfix?: string;
		cacheTtl?: number;
		resetCache?: boolean;
	} & (
		| {
				url: string;
				path?: string;
		  }
		| {
				url?: string;
				path: string;
		  }
	),
) => (input?: TInput) => Promise<TOutput>;

export const createAPIMethod: CreateAPIMethod = (args) => (input) => {
	const {
		apiPath = '/api/v1',
		method,
		url,
		path,
		headers,
		normalize = (response) =>
			'results' in response || 'result' in response
				? normalizeToCamelCase(response.results || response.result)
				: normalizeToCamelCase(response),
		skipResponseParsing,
		suppressErrorThrowing,
		requestTimeout = REQUEST_TIMEOUT,
		requestId = crypto.randomUUID(),
		abortController = abortControllerPool.getController(requestId),
		useCache = false,
		cacheKeyPostfix,
		cacheTtl = CACHE_TTL,
		resetCache = false,
	} = args;
	const timeFrom = globalThis.performance.now();
	const requestHeaders = prepareHeaders(headers, requestId, useCache);
	const fetchUrl = prepareUrl(apiPath, path, url);

	const requestUID = {
		correlationId: (requestHeaders as Record<string, string>)?.['x-correlation-id'] ?? '',
		requestId,
		timeFrom,
	};

	logTimes(requestUID, { uri: path || fetchUrl, input, response_status: 100 });

	const cachedUrlKey =
		`${fetchUrl}?${new URLSearchParams(input).toString()}` + (cacheKeyPostfix ? `_${cacheKeyPostfix}` : '');

	if (isSSR() && useCache && !resetCache) {
		const cached = getCache<any>(cachedUrlKey);

		if (cached) {
			logTimes(requestUID, { cache: 'Fetch:HIT', uri: path || fetchUrl, input, response_status: 200 });
			abortControllerPool.releaseController(requestId);
			return new Promise((resolve) => resolve(cached));
		}

		logTimes(requestUID, { cache: 'Fetch:MISS', uri: path || fetchUrl, input, response_status: 204 });
	}

	const timeoutId = setTimeout(() => {
		logTimes(requestUID, { response_status: 408, uri: path || fetchUrl, input });
		abortControllerPool.abort(requestId);
	}, requestTimeout);
	const requestInit = {
		headers: requestHeaders,
		signal: abortController.signal,
	};

	return methods[method](fetchUrl, input, requestInit)
		.finally(() => {
			clearTimeout(timeoutId);
			abortControllerPool.releaseController(requestId);
		})
		.catch((e) => {
			if (isSSR()) {
				logErrorTimes(
					requestUID,
					`: Failed to fetch: ${fetchUrl}, with: ${input ? JSON.stringify(input) : 'No request input.'}`,
					e,
				);

				outgoingMetrics.measureOutgoingRequestDuration(
					{
						endpoint: getEndpoint(apiPath, path, url),
						type: 'REST',
						method,
						remote_app: getRemoteApp(fetchUrl) ?? '',
						status_code:
							e instanceof ApiError
								? e.cause.status.toString()
								: e instanceof Error && e.name === 'AbortError'
									? '299'
									: '500',
					},
					globalThis.performance.now() - timeFrom,
				);
			}

			throw e;
		})
		.then((res) => {
			if (isSSR()) {
				logTimes(requestUID, { response_status: res.status, uri: path || fetchUrl, input });

				outgoingMetrics.measureOutgoingRequestDuration(
					{
						endpoint: getEndpoint(apiPath, path, url),
						type: 'REST',
						method,
						remote_app: getRemoteApp(fetchUrl) ?? '',
						status_code: res.status.toString(),
					},
					globalThis.performance.now() - timeFrom,
				);
			}

			if (res.status >= 400) {
				if (!suppressErrorThrowing) {
					throw new ApiError(
						`API Error for ${fetchUrl}, with: ${input ? JSON.stringify(input) : 'No request input.'} \n${res.status} ${res.statusText}\n${JSON.stringify(requestInit)}`,
						{ cause: res, url: res.url, input },
					);
				}

				return res
					.json()
					.then(normalizeToCamelCase)
					.catch((e) => {
						logErrorTimes(requestUID, `Failed to parse response: ${fetchUrl}`, e);
					});
			}

			if (skipResponseParsing) {
				if (isSSR() && useCache) {
					setCache(cachedUrlKey, structuredClone(res.clone()), cacheTtl);
				}

				return res;
			}

			return res.json().then((response) => {
				const data = normalize(response);

				if (isSSR() && useCache) {
					setCache(cachedUrlKey, structuredClone(data), cacheTtl);
				}

				return data;
			});
		});
};

const convertHeadersToObject = (headers: Headers | HeadersInit | undefined): Record<string, string> => {
	const result: Record<string, string> = {};
	if (headers instanceof Headers) {
		headers.forEach((value, key) => {
			result[key] = value;
		});
	} else if (headers) {
		Object.entries(headers).forEach(([key, value]) => {
			result[key] = value;
		});
	} else {
		return {};
	}

	return result;
};

const addDefaultHeaders = (
	headers: Record<string, string>,
	useCache: boolean,
	requestId: string,
): Record<string, string> => {
	const newHeaders = { ...headers };

	newHeaders['x-service-version'] = config.version;
	newHeaders['x-request-id'] = requestId;

	if (!useCache) {
		newHeaders['cache-control'] = 'no-cache, no-store, must-revalidate';
		newHeaders['pragma'] = 'no-cache';
		newHeaders['expires'] = '0';
	}

	if (!('cookie' in newHeaders)) {
		newHeaders['cookie'] = globalThis?.document?.cookie ?? '';
	}

	if (isSSR()) {
		newHeaders['x-server-side-rendering'] = 'true';
		newHeaders['x-service-name'] = 'sbazar';
		if (!headers) {
			newHeaders['x-forwarded-for'] = '127.0.0.1, ::1';
		}
		if (!headers['referer'] && globalThis?.location?.href) {
			newHeaders['referer'] = enforceHttpsProtocol(globalThis?.location?.href);
		}
	}

	return newHeaders;
};

const prepareHeaders = (headers: undefined | HeadersInit, requestId: string, useCache: boolean): HeadersInit => {
	let headersObject = convertHeadersToObject(headers);

	headersObject = addDefaultHeaders(headersObject, useCache, requestId);

	// Encode headers
	return encodeHeaders(headersObject);
};

const blockerWordsRegex = /items(?!_list)/;
function denormalizeBlockerWords(path: string) {
	// items_list is for statistic endpoint /items/statistics/items_list, leave it as it is
	return path.replace(blockerWordsRegex, 'adverts');
}

const prepareUrl = (apiPath: string, path = '', url?: string) => {
	if (url) return url;

	if (isSSR()) {
		return `http://${process.env.CONF_BACKEND || 'localhost:80'}${apiPath}${denormalizeBlockerWords(path)}`;
	}

	return `${globalThis.window.location.origin}${apiPath}${path}`;
};

const obfuscateRegex = /(\w*\d\w+)|(((?:\/))\d((?:\/|$)))/g;
const getEndpoint = (apiPath: string, path = '', url?: string) => {
	if (url) return url;

	return `${apiPath}${denormalizeBlockerWords(path).replace(obfuscateRegex, '*')}`;
};

const getRemoteApp = (url: string) => {
	if (url.includes('ribbon')) {
		return process.env.CONF_RIBBON_URL;
	}

	return process.env.CONF_BACKEND;
};
